import React, {
  useEffect,
  ReactNode,
  useState,
  useMemo,
  useCallback,
  memo,
} from "react";
import Hamburger from "./Hamburger";
import { ReactComponent as GNAiIcon } from "../icons/gnai_logo.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  addTabs,
  setShowTabs,
  setDisabledTabs,
} from "../store/state/SwitchTabs";
import { FilterTab } from "./Filters";
import { SearchBox } from "./Search";
import {
  setDefaultDates,
  updateDefaultDates,
  updateDefaultTrendDates,
} from "../store/state/DefaultDatesSetter";
import { formatDateForAPI } from "../pages/Rajasthan/Utility/const";
import { DynamicDatePicker } from "./DatePicker";
import type { RootState } from "../store/store";

interface TabConfig {
  [key: string]: ReactNode;
}

interface DynamicTabProps {
  headerPrefixComponent?: ReactNode;
  header: string;
  tabs: TabConfig;
  showTabs?: boolean;
  middleComponent?: ReactNode;
  trendTabs?: string[];
  disabledTabs?: string[];
}

interface NewDynamicTabProps {
  headerPrefixComponent?: ReactNode;
  header: string;
  tabs: TabConfig;
  showTabs?: boolean;
  middleComponent?: ReactNode;
  trendTabs?: string[];
  defaultDates?: Record<string, any>;
  hideDateFilterOnTabs?: string[];
  showDates?: boolean;
  minDate?: string;
  headerHeight?: string;
  showReactSelect?: boolean;
  disabledTabs?: string[];
  rightComponent?: ReactNode;
  disableDatePicker?: boolean;
}

export function DynamicTab({
  headerPrefixComponent,
  header,
  tabs,
  middleComponent,
  showTabs = true,
  disabledTabs = [],
}: DynamicTabProps) {
  const tabNames = useMemo(() => Object.keys(tabs), [tabs]);
  const initialTab = useMemo(
    () => new URLSearchParams(window.location.search).get("tab") || tabNames[0],
    // intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [selectedTab, setSelectedTab] = useState(initialTab);

  const handleTabChange = useCallback(
    (option: string) => {
      if (disabledTabs.includes(option)) return;
      setSelectedTab(option);
      const url = new URL(window.location.href);
      url.searchParams.set("tab", option);
      window.history.pushState({}, "", url);
    },
    [disabledTabs],
  );

  useEffect(() => {
    const handlePopState = () => {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (tab && tabNames.includes(tab)) {
        setSelectedTab(tab);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [tabNames]);

  const tabsRight = useMemo(() => {
    if (!showTabs) return <div className="w-40" />;
    if (tabNames.length === 1)
      return <div className="tab tab-active rounded-lg">{tabNames[0]}</div>;
    return (
      <FilterTab
        options={tabNames}
        selected={[selectedTab]}
        onClick={handleTabChange}
        disabledOptions={disabledTabs}
      />
    );
  }, [showTabs, tabNames, selectedTab, handleTabChange, disabledTabs]);

  const leftComponent = useMemo(
    () => (
      <div className="flex items-center gap-5">
        <GNAiIcon width={80} />
        <Hamburger />
        {headerPrefixComponent}
        <h1 className="text-[18px] text-[#6F6D7C]">{header}</h1>
      </div>
    ),
    [headerPrefixComponent, header],
  );

  return (
    <div className="page-wrapper">
      <PageHeader
        leftComponent={leftComponent}
        middleComponent={middleComponent}
        rightComponent={tabsRight}
      />
      <div className="tabContent">
        <div className="flex flex-row w-full h-full">
          {tabs[selectedTab] || <div>Invalid tab selected</div>}
        </div>
      </div>
    </div>
  );
}

export function NewDynamicTab({
  headerHeight,
  headerPrefixComponent,
  hideDateFilterOnTabs = [],
  header,
  tabs,
  middleComponent,
  showDates = true,
  trendTabs,
  defaultDates,
  showTabs = true,
  showReactSelect = false,
  minDate,
  disabledTabs = [],
  rightComponent,
  disableDatePicker = false,
}: NewDynamicTabProps) {
  const activeTab = useSelector(
    (state: RootState) => state.switchTabs.activeTab,
  );
  // Select only the active tab's dates — avoids re-render when other tabs' data changes
  const activeTabDates = useSelector(
    (state: RootState) =>
      state.defaultDatesSetter.defaultDates?.[activeTab] ?? null,
  );
  // Needed for existence check only — full map required
  const DefaultDates = useSelector(
    (state: RootState) => state.defaultDatesSetter.defaultDates,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (defaultDates) {
      dispatch(setDefaultDates(defaultDates));
    }
  }, [dispatch, defaultDates]);

  useEffect(() => {
    if (tabs && Object.keys(tabs).length > 0) {
      dispatch(addTabs(tabs));
      dispatch(setShowTabs(showTabs));
      dispatch(setDisabledTabs(disabledTabs));
    }
  }, [dispatch, showTabs, tabs, disabledTabs]);

  const existingTrendDates: any[] = useMemo(
    () => activeTabDates?.defaultTrendDates ?? [],
    [activeTabDates?.defaultTrendDates],
  );

  const addDate = useCallback(
    (date: any) => {
      const formattedStart = formatDateForAPI(date.startDate);
      const formattedEnd = formatDateForAPI(date.endDate);
      const newDateValue = `${formattedStart},${formattedEnd}`;

      if (existingTrendDates.some((d: any) => d.value === newDateValue)) return;

      dispatch(
        updateDefaultTrendDates({
          activeTab,
          dates: existingTrendDates.concat([
            {
              label: `${formattedStart} to ${formattedEnd}`,
              value: newDateValue,
            },
          ]),
        }),
      );
    },
    [dispatch, activeTab, existingTrendDates],
  );

  const onTrendDatesChange = useCallback(
    (item: any[]) => {
      dispatch(updateDefaultTrendDates({ activeTab, dates: item ?? [] }));
    },
    [dispatch, activeTab],
  );

  const onRangeDateChange = useCallback(
    (item: any) => {
      dispatch(updateDefaultDates({ activeTab, dates: item }));
    },
    [dispatch, activeTab],
  );

  const onPickerDateChange = useCallback(
    (date: any[]) => {
      dispatch(updateDefaultDates({ activeTab, dates: date[0]! }));
    },
    [dispatch, activeTab],
  );

  const shouldShowDateFilter =
    !hideDateFilterOnTabs.includes(activeTab) &&
    !activeTabDates?.hideDateFilter;

  const showDateRange = activeTabDates?.showDateRange ?? false;

  const defaultRightContent = useMemo(
    () => (
      <>
        {showDates &&
          DefaultDates &&
          shouldShowDateFilter &&
          (trendTabs?.includes(activeTab) ? (
            <div className="flex items-center gap-2">
              <DynamicDatePicker
                setDate={(date) => addDate(date[0]!)}
                disabled={disableDatePicker}
              />
              <SearchBox
                options={activeTabDates?.trendDateOptions || []}
                isMany={true}
                placeholder="Date Range"
                selectedOptions={existingTrendDates}
                onChange={onTrendDatesChange}
              />
            </div>
          ) : showDateRange ? (
            <SearchBox
              options={activeTabDates?.dateRangeDateOptions || []}
              isMany={false}
              placeholder="Date Range"
              selectedOptions={
                activeTabDates?.defaultRangeDate
                  ? [activeTabDates.defaultRangeDate]
                  : []
              }
              onChange={onRangeDateChange}
            />
          ) : (
            <DynamicDatePicker
              setDate={onPickerDateChange}
              minDate={minDate}
              defaultDates={activeTabDates?.defaultRangeDate || []}
              disabled={disableDatePicker}
            />
          ))}

        {showReactSelect && DefaultDates && shouldShowDateFilter && (
          <SearchBox
            options={activeTabDates?.trendDateOptions || []}
            isMany={true}
            placeholder={activeTabDates?.datePlaceholder || "Date Range"}
            selectedOptions={existingTrendDates}
            onChange={onTrendDatesChange}
          />
        )}
      </>
    ),
    [
      showDates,
      DefaultDates,
      shouldShowDateFilter,
      trendTabs,
      activeTab,
      activeTabDates,
      existingTrendDates,
      showDateRange,
      showReactSelect,
      minDate,
      addDate,
      onTrendDatesChange,
      onRangeDateChange,
      onPickerDateChange,
      disableDatePicker,
    ],
  );

  const leftComponent = useMemo(
    () => (
      <div className="flex items-center gap-5">
        <GNAiIcon width={80} />
        <Hamburger />
        {headerPrefixComponent}
        <h1 className="text-[18px] text-[#6F6D7C]">{header}</h1>
      </div>
    ),
    [headerPrefixComponent, header],
  );

  return (
    <div className="page-wrapper">
      <PageHeader
        headerHeight={headerHeight}
        leftComponent={leftComponent}
        middleComponent={middleComponent}
        rightComponent={rightComponent ?? defaultRightContent}
      />

      {activeTab && (
        <div className="tabContent">
          <div key={activeTab}>
            {tabs[activeTab] || <div>Invalid tab selected</div>}
          </div>
        </div>
      )}
    </div>
  );
}

interface PageHeaderProps {
  leftComponent?: ReactNode;
  middleComponent?: ReactNode;
  rightComponent?: ReactNode;
  headerHeight?: string;
}

export const PageHeader = memo(function PageHeader({
  leftComponent,
  middleComponent,
  rightComponent,
  headerHeight,
}: PageHeaderProps) {
  return (
    <div
      className={`newheader grid grid-cols-3 gap-3 justify-between items-center relative z-[99] ${headerHeight ?? ""}`}
    >
      <div className="col-span-1 flex items-center gap-5">{leftComponent}</div>
      <div className="col-span-1 flex justify-center">{middleComponent}</div>
      <div className="col-span-1 flex justify-end">{rightComponent}</div>
    </div>
  );
});
